// Top level libraries and 3rd party dependencies
import React, { Component } from 'react';
import { func, object, string } from 'prop-types';
import { contains, isEmpty } from 'ramda';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'react-feather';

import * as SessionTypes from '../../constants/ActionTypes';
import { MAX_VERIFY_WITH_NAME_RETRY_COUNT, DEFAULT_HIPAA_SAFE_LABEL} from '../../constants/AppConstants';
import Layout from '../Layout';
import LoadingState from '../LoadingState';
import ErrorMessage from '../ErrorMessage';
import SavePIN from '../SavePIN';
import Footer from '../Footer';

// Page styles
import cx from 'classnames';
import s from './Verify.css';


// Updox Contacts require validating their first and last name first before setting a PIN.
export default class VerifyContact extends Component {
    static propTypes = {
        conversationDetails: object.isRequired,
        conversationHash: string.isRequired,
        loadingState: object,
        savePIN: func,
        session: object.isRequired,
        verifyName: func.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            firstName: '',
            lastName: '',
        };
    }

    handleChange = event => this.setState({ [event.target.name]: event.target.value });

    // Tests will fail if we do not give this Ramda method what it expects, an array. this.props.loadingState.requests returns an array.
    isLoadingVerifyName = loadingState => contains(SessionTypes.VERIFY_NAME_REQUEST, loadingState.requests || []);

    handleSubmit = e => {
        e.preventDefault();

        const { conversationHash, verifyName } = this.props;
        const { firstName, lastName } = this.state;

        if (this.canBeSubmitted()) {
            const request = {
                conversationHash,
                firstName,
                lastName,
            };

            verifyName(request);
            e.target.blur();
        }
    };

    canBeSubmitted = () => {
        const { conversationHash } = this.props;
        const { firstName, lastName } = this.state;

        return (
            firstName.length > 0 &&
            lastName.length > 0 &&
            !isEmpty(conversationHash)
        );
    };

    render() {
        const {
            conversationHash,
            conversationDetails,
            loadingState,
            savePIN,
            session,
        } = this.props;

        const { firstName, lastName } = this.state;

        const { expiredConversation } = conversationDetails;
        const hipaaSafePracticeName = `${conversationDetails.practiceName}` === DEFAULT_HIPAA_SAFE_LABEL
            ? `your ` + DEFAULT_HIPAA_SAFE_LABEL : `${conversationDetails.practiceName}`;
        const hasConversationDetails = !isEmpty(conversationDetails);
        const lockedOut = session.error ? session.error.retryCount >= MAX_VERIFY_WITH_NAME_RETRY_COUNT : false;
        const additionalLockedOutCopy = `${conversationDetails.practiceName}` === DEFAULT_HIPAA_SAFE_LABEL
            ? `Your ${conversationDetails.practiceName} has been notified of the failure to authenticate.`
            : `${conversationDetails.practiceName} has been notified of the failure to authenticate.`;
        const additionalPracticePhoneCopy = conversationDetails.practicePhone ? ` at ${conversationDetails.practicePhone}.` : '.';
        const isEnabled = this.canBeSubmitted();
        const isLoadingVerifyName = this.isLoadingVerifyName(loadingState);
        const resetForgottenPINMessage = `${session.message}`;

        return (
            <Layout centered className={s.layout}>
                {!session.verifiedName &&
                    <form
                        className={cx(s.component, session.error ? 'hasErrors' : '')}
                        id="verifyNameForm"
                        onSubmit={this.handleSubmit}
                    >
                        {hasConversationDetails && !lockedOut &&
                            <div id="conversationDetails" className={cx(s.alignItems, s.componentBlock, s.verticalSpacing, s.heading)}>
                                <h3>{conversationDetails.practiceName}</h3>
                                <label>{conversationDetails.practicePhone}</label>
                            </div>
                        }

                        {hasConversationDetails && expiredConversation &&
                            <ErrorMessage
                                title="This conversation has expired"
                                message={
                                    conversationDetails.canReply ? `You may still send a message to the practice with the button below. If you have any immediate concerns, please contact ${hipaaSafePracticeName}${additionalPracticePhoneCopy}`
                                        : `If you have any immediate concerns, please contact ${hipaaSafePracticeName}${additionalPracticePhoneCopy}`
                                }
                            />
                        }

                        {expiredConversation && conversationDetails.canReply &&
                            <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing)}>
                                <Link
                                    id="sendMessageToPractice"
                                    className={cx(s.btn, s.primary, s.link)}
                                    to={`/expired/${conversationHash}`}
                                >
                                    <span className={s.btnText}>Send Message</span>
                                </Link>
                            </div>
                        }

                        {!lockedOut && !expiredConversation &&
                            <React.Fragment>
                                <hr className={s.divider}/>

                                <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing, s.heading)}>
                                    {session.message &&
                                        <div>
                                            <CheckCircle className={s.icon} size={25} />
                                            <p>{resetForgottenPINMessage}</p>
                                            <br />
                                        </div>
                                    }
                                    <p>Welcome! Let's confirm your full name and create a PIN to view your message.</p>
                                </div>
                            </React.Fragment>
                        }

                        {!lockedOut && !expiredConversation && hasConversationDetails &&
                            <React.Fragment>
                                <div className={cx('formGroup', s.verticalSpacing)}>
                                    <div className={cx(s.componentBlock, 'fullWidth')}>
                                        <input
                                            id="firstName"
                                            type="text"
                                            name="firstName"
                                            value={firstName}
                                            onChange={this.handleChange}
                                            className="inputControl"
                                            autoComplete="off"
                                            required
                                        />
                                        <label
                                            htmlFor="firstName"
                                            className={s.heading}
                                        >
                                            <span>First Name</span>
                                        </label>
                                    </div>
                                </div>
                                <div className={cx('formGroup', s.verticalSpacing)}>
                                    <div className={cx(s.componentBlock, 'fullWidth')}>
                                    <input
                                        id="lastName"
                                        type="text"
                                        name="lastName"
                                        value={lastName}
                                        onChange={this.handleChange}
                                        className="inputControl"
                                        autoComplete="off"
                                        required
                                    />
                                    <label
                                        htmlFor="lastName"
                                        className={s.heading}
                                    >
                                        <span>Last Name</span>
                                    </label>
                                    </div>
                                </div>
                                <p className={s.terms}>By proceeding I accept the&nbsp;
                                    <Link
                                        id="viewTermsAndConditions"
                                        className={s.link}
                                        to="//www.updox.com/termsofservice"
                                        target="_blank"
                                    >
                                        Terms &amp; Conditions
                                    </Link>
                                </p>
                            </React.Fragment>
                        }

                        {session.error && hasConversationDetails &&
                            <ErrorMessage
                                title="Failure to authenticate"
                                message={`${session.error.message} ${lockedOut ? additionalLockedOutCopy : ''} If you have any questions or concerns, please contact ${hipaaSafePracticeName}${additionalPracticePhoneCopy}`}
                            />
                        }

                        {!lockedOut && !expiredConversation && hasConversationDetails &&
                            <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing)}>
                                <button
                                    className={cx(s.btn, s.primary)}
                                    id="submitVerifyName"
                                    type="submit"
                                    disabled={!isEnabled || isLoadingVerifyName}
                                >
                                    {isLoadingVerifyName ?
                                        <LoadingState context="verifyForm"/> :
                                        <span className={s.btnText}>Next</span>
                                    }
                                </button>
                            </div>
                        }

                        <Footer/>
                    </form>
                }

                {hasConversationDetails && !isEmpty(conversationHash) && session.verifiedName &&
                    <SavePIN
                        conversationHash={conversationHash}
                        error={session.error}
                        loadingState={loadingState}
                        conversationDetails={conversationDetails}
                        savePIN={savePIN}
                    />
                }
            </Layout>
        );
    }
}